import blocksConfig from '@assets/config/blocks.json';
import Matter from 'matter-js';
import type { Container, Texture } from 'pixi.js';
import { Assets } from 'pixi.js';
import {
  BlockGeometryCalculator,
  FallingBlock,
} from '@/canvas/components/FallingBlock';
import {
  calculateGeometricCenterOfMass,
  fetchSvgElement,
  normalizeAndScaleVertices,
  parseSvgViewBox,
} from '@/canvas/systems/blockSvgUtils';
import type {
  BlockConfig,
  FallingBlockData,
  IPhysicsEngine,
} from '@/canvas/types/Block';
import { useGameStore } from '@/store/gameStore';
import { imagePathMap, svgPathMap } from '@/utils/blockAssets';

/**
 * 落ちもの管理システム
 */
export class BlockManager {
  /** スポーン対象とするブロック種類の最大インデックス（0始まり）。最初の3種のみ出現。 */
  private static readonly MAX_SPAWNABLE_BLOCK_INDEX = 2;

  private blockConfigs: BlockConfig[] = [];
  private blockDataCache: Map<number, FallingBlockData> = new Map();
  private currentBlock: FallingBlock | null = null;
  private nextDropBlockId: number | null = null;
  private boxX: number;
  private boxWidth: number;
  private spawnY: number;

  constructor(
    boxX: number,
    boxY: number,
    boxWidth: number,
    spawnOffsetAboveBox: number = 80,
  ) {
    this.boxX = boxX;
    this.boxWidth = boxWidth;
    this.spawnY = boxY - spawnOffsetAboveBox;
  }

  async loadBlockConfigs(): Promise<void> {
    try {
      this.blockConfigs = (blocksConfig as BlockConfig[]).sort(
        (a, b) => a.id - b.id,
      );

      await this.preloadBlockData();
    } catch (error) {
      console.error('Failed to load block configs:', error);
      throw error;
    }
  }

  private async preloadBlockData(): Promise<void> {
    for (const config of this.blockConfigs) {
      try {
        const imagePath = this.normalizeAssetPath(config.imagePath);
        const svgPath = this.normalizeAssetPath(config.verticesPath);

        const texture = (await Assets.load(imagePath)) as Texture;

        const { vertices, centerOfMass } = await this.loadVerticesFromSvg(
          svgPath,
          texture,
        );

        const blockData: FallingBlockData = {
          blockId: config.id,
          name: config.name,
          texture: texture,
          centerOfMassX: centerOfMass.x,
          centerOfMassY: centerOfMass.y,
          vertices: vertices,
          score: config.score,
          size: config.size ?? 1.0,
        };

        this.blockDataCache.set(config.id, blockData);
      } catch (error) {
        console.error(`Failed to load block ${config.id}:`, error);
      }
    }
  }

  private normalizeAssetPath(path: string): string {
    if (path.startsWith('/')) {
      return path;
    }

    if (imagePathMap[path]) {
      return imagePathMap[path];
    }

    if (svgPathMap[path]) {
      return svgPathMap[path];
    }

    console.warn(`Path mapping not found for: ${path}, using fallback`);
    return `/assets/${path}`;
  }

  private async loadVerticesFromSvg(
    svgPath: string,
    texture: Texture,
  ): Promise<{
    vertices: Array<{ x: number; y: number }>;
    centerOfMass: { x: number; y: number };
  }> {
    const svgElement = await fetchSvgElement(svgPath);
    const { width: svgWidth, height: svgHeight } = parseSvgViewBox(svgElement);

    const allVertices =
      await BlockGeometryCalculator.getVerticesFromSvg(svgPath);
    let vertices = allVertices.length > 0 ? allVertices[0] : [];

    if (vertices.length === 0) {
      console.warn(`No vertices found in SVG: ${svgPath}, using fallback`);
    }

    const pngWidth = texture.source.width;
    const pngHeight = texture.source.height;

    if (vertices.length > 0) {
      vertices = normalizeAndScaleVertices(
        vertices,
        svgWidth,
        svgHeight,
        pngWidth,
        pngHeight,
      );
    }

    const centerOfMass =
      vertices.length > 0
        ? calculateGeometricCenterOfMass(vertices)
        : BlockGeometryCalculator.calculateCenterOfMass(texture);

    return { vertices, centerOfMass };
  }

  /**
   * スポーン可能なブロック種類からランダムに1つ選んでIDを返す。
   * MAX_SPAWNABLE_BLOCK_INDEX の範囲内に限定する。
   */
  private pickRandomSpawnableBlockId(): number | null {
    const availableBlocks = Array.from(this.blockDataCache.keys());
    if (availableBlocks.length === 0) return null;

    const maxIndex = Math.min(
      BlockManager.MAX_SPAWNABLE_BLOCK_INDEX,
      availableBlocks.length - 1,
    );
    const randomIndex = Math.floor(Math.random() * (maxIndex + 1));
    return availableBlocks[randomIndex];
  }

  /**
   * 指定データのブロックをスポーン位置に生成し、container と activeBlocks に登録する。
   * createRandomBlock / createBlockById の共通処理。
   */
  private spawnBlock(
    blockData: FallingBlockData,
    physicsEngine: IPhysicsEngine,
    container: Container,
    activeBlocks?: Set<FallingBlock>,
  ): FallingBlock {
    const x = this.boxX + this.boxWidth / 2;
    const block = new FallingBlock(blockData, physicsEngine, x, this.spawnY);
    container.addChild(block);
    activeBlocks?.add(block);
    this.currentBlock = block;
    return block;
  }

  createRandomBlock(
    physicsEngine: IPhysicsEngine,
    container: Container,
    activeBlocks?: Set<FallingBlock>,
  ): FallingBlock | null {
    const randomBlockId = this.pickRandomSpawnableBlockId();
    if (randomBlockId === null) {
      console.warn('No blocks available');
      return null;
    }

    const blockData = this.blockDataCache.get(randomBlockId);
    if (!blockData) {
      return null;
    }

    return this.spawnBlock(blockData, physicsEngine, container, activeBlocks);
  }

  getCurrentBlock(): FallingBlock | null {
    return this.currentBlock;
  }

  updateCurrentBlockPosition(mouseX: number): void {
    if (!this.currentBlock) {
      return;
    }

    const minX = this.boxX + this.currentBlock.width / 2;
    const maxX = this.boxX + this.boxWidth - this.currentBlock.width / 2;
    const clampedX = Math.max(minX, Math.min(maxX, mouseX));

    const y = this.spawnY;

    Matter.Body.setPosition(this.currentBlock.getMatterBody(), {
      x: clampedX,
      y: y,
    });

    this.currentBlock.x = clampedX;
    this.currentBlock.y = y;
  }

  dropCurrentBlock(): void {
    if (this.currentBlock) {
      this.currentBlock.startFalling();
      this.currentBlock = null;
    }
  }

  getNextBlockId(blockId: number): number | null {
    const nextId = blockId + 1;
    if (this.blockDataCache.has(nextId)) {
      return nextId;
    }
    return null;
  }

  getBlockData(blockId: number): FallingBlockData | null {
    return this.blockDataCache.get(blockId) || null;
  }

  selectNextDropId(): number | null {
    const id = this.pickRandomSpawnableBlockId();
    this.nextDropBlockId = id;
    useGameStore.getState().setNextDropBlockId(id);
    return id;
  }

  getNextDropBlockId(): number | null {
    return this.nextDropBlockId;
  }

  createBlockById(
    blockId: number,
    physicsEngine: IPhysicsEngine,
    container: Container,
    activeBlocks?: Set<FallingBlock>,
  ): FallingBlock | null {
    const blockData = this.blockDataCache.get(blockId);
    if (!blockData) {
      console.warn(`Block data not found for id: ${blockId}`);
      return null;
    }

    return this.spawnBlock(blockData, physicsEngine, container, activeBlocks);
  }

  getAvailableBlockCount(): number {
    return this.blockDataCache.size;
  }
}
